const pathrohiMobileFooter = window.matchMedia('(max-width: 560px)');
const pathrohiReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initializePathrohiFooter(footer) {
  if (!(footer instanceof HTMLElement) || footer.dataset.pathrohiAccordionsReady === 'true') return;

  const groups = Array.from(footer.querySelectorAll('[data-pathrohi-footer-accordion]'));
  if (!groups.length) return;

  const sync = () => {
    const isMobile = pathrohiMobileFooter.matches;

    groups.forEach((group, index) => {
      const button = group.querySelector('[data-pathrohi-footer-trigger]');
      const panel = group.querySelector('[data-pathrohi-footer-panel]');
      if (!(button instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

      if (!isMobile) {
        panel.hidden = false;
        return;
      }

      if (!group.dataset.pathrohiInitialized) {
        const expanded = index === 0;
        button.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
        group.dataset.pathrohiInitialized = 'true';
      } else {
        panel.hidden = button.getAttribute('aria-expanded') !== 'true';
      }
    });
  };

  groups.forEach((group) => {
    const button = group.querySelector('[data-pathrohi-footer-trigger]');
    const panel = group.querySelector('[data-pathrohi-footer-panel]');
    if (!(button instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

    button.addEventListener('click', () => {
      if (!pathrohiMobileFooter.matches) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
    });
  });

  pathrohiMobileFooter.addEventListener('change', sync);
  footer.dataset.pathrohiAccordionsReady = 'true';
  sync();
}

function initializePathrohiMediaCarousel(carousel) {
  if (!(carousel instanceof HTMLElement) || carousel.dataset.pathrohiCarouselReady === 'true') return;

  const viewport = carousel.querySelector('[data-pathrohi-media-viewport]');
  const slides = Array.from(carousel.querySelectorAll('[data-pathrohi-media-slide]'));
  const previous = carousel.querySelector('[data-pathrohi-media-previous]');
  const next = carousel.querySelector('[data-pathrohi-media-next]');
  const current = carousel.querySelector('[data-pathrohi-media-current]');
  const initialIndex = Number.parseInt(carousel.dataset.pathrohiInitialIndex || '0', 10);

  if (!(viewport instanceof HTMLElement) || !slides.length) return;
  if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

  let selectedIndex = 0;
  let frame = 0;

  const update = (index) => {
    selectedIndex = Math.max(0, Math.min(index, slides.length - 1));
    previous.disabled = selectedIndex === 0;
    next.disabled = selectedIndex === slides.length - 1;

    if (current) current.textContent = String(selectedIndex + 1);
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-current', String(slideIndex === selectedIndex));
    });
  };

  const nearestSlide = () => {
    const viewportLeft = viewport.scrollLeft;
    let nearest = 0;
    let distance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const nextDistance = Math.abs(slide.offsetLeft - viewport.offsetLeft - viewportLeft);
      if (nextDistance < distance) {
        distance = nextDistance;
        nearest = index;
      }
    });

    update(nearest);
  };

  const goTo = (index) => {
    const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
    if (!(target instanceof HTMLElement)) return;

    viewport.scrollTo({
      left: target.offsetLeft - viewport.offsetLeft,
      behavior: pathrohiReducedMotion.matches ? 'auto' : 'smooth',
    });
    update(slides.indexOf(target));
  };

  viewport.addEventListener(
    'scroll',
    () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(nearestSlide);
    },
    { passive: true }
  );

  viewport.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(selectedIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(selectedIndex + 1);
    }
  });

  previous.addEventListener('click', () => goTo(selectedIndex - 1));
  next.addEventListener('click', () => goTo(selectedIndex + 1));

  carousel.addEventListener('pathrohi:select-media', (event) => {
    const mediaId = event.detail?.mediaId;
    const index = slides.findIndex((slide) => slide.dataset.pathrohiMediaSlide === String(mediaId));
    if (index >= 0) goTo(index);
  });

  carousel.dataset.pathrohiCarouselReady = 'true';
  update(Number.isNaN(initialIndex) ? 0 : initialIndex);
  if (initialIndex > 0) requestAnimationFrame(() => goTo(initialIndex));
}

function initializePathrohiStage3(root = document) {
  root.querySelectorAll('.pathrohi-footer').forEach(initializePathrohiFooter);
  root.querySelectorAll('[data-pathrohi-media-carousel]').forEach(initializePathrohiMediaCarousel);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializePathrohiStage3());
} else {
  initializePathrohiStage3();
}

document.addEventListener('shopify:section:load', (event) => initializePathrohiStage3(event.target));

function pathrohiCartRoute(path) {
  const root = window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  return `${normalizedRoot}${String(path).replace(/^\/+/, '')}`;
}

async function handlePathrohiQuickAdd(form) {
  const button = form.querySelector('[data-pathrohi-quick-add]');
  const label = form.querySelector('[data-pathrohi-quick-add-label]');
  const feedback = form.querySelector('[data-pathrohi-cart-feedback]');
  if (!(button instanceof HTMLButtonElement) || !(feedback instanceof HTMLElement)) return;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  feedback.classList.add('hidden');
  feedback.replaceChildren();
  if (label) label.textContent = 'Adding…';

  try {
    const addResponse = await fetch(pathrohiCartRoute('cart/add.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: new FormData(form),
    });

    const addedItem = await addResponse.json();
    if (!addResponse.ok) throw new Error(addedItem.description || addedItem.message || 'This piece could not be added.');

    const cartResponse = await fetch(pathrohiCartRoute('cart.js'), {
      headers: { Accept: 'application/json' },
    });
    if (!cartResponse.ok) throw new Error('The bag count could not be refreshed.');
    const cart = await cartResponse.json();

    const cartIcon = document.querySelector('cart-icon');
    if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
      cartIcon.renderCartBubble(cart.item_count, false);
    }

    const cartLink = document.createElement('a');
    cartLink.href = pathrohiCartRoute('cart');
    cartLink.textContent = 'View bag';
    feedback.replaceChildren(document.createTextNode('Added to your bag. '), cartLink);
  } catch (error) {
    feedback.textContent = error instanceof Error ? error.message : 'This piece could not be added. Please try again.';
  } finally {
    feedback.classList.remove('hidden');
    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (label) label.textContent = 'Add to Bag';
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target instanceof Element ? event.target.closest('[data-pathrohi-quick-add-form]') : null;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    handlePathrohiQuickAdd(form);
  },
  true
);
